import type { Ref } from "vue";

import { defineStore } from "pinia";
import { computed, ref } from "vue";

import { reportException } from "@/sentry";

export async function executeAbortableLoad<R>(
  abortableStagedLoad: AsyncGenerator<AbortableRequestStages<R>>,
  intercepts: {
    onPre?: (stage: PreRequestStage) => void;
    onPost?: (stage: PostRequestStage<R>) => void;
    onResponded: (stage: RespondedRequestStage<R>) => void;
    onError: (stage: ErrorRequestStage) => void;
    onTimeout?: (stage: TimeoutRequestStage) => void;
  },
): Promise<void> {
  const { onPre, onPost, onResponded, onError, onTimeout } = intercepts;
  for await (const stage of abortableStagedLoad) {
    switch (stage.phase) {
      case "pre":
        onPre?.(stage);
        break;
      case "post":
        onPost?.(stage);
        break;
      case "responded":
        onResponded(stage);
        break;
      case "error":
        onError(stage);
        break;
      case "timeout":
        onTimeout?.(stage);
        break;
    }
  }
}

export type ErrorRequestStage = {
  phase: "error";
  abortController: AbortController;
  error: Error;
};
export type RespondedRequestStage<R> = {
  phase: "responded";
  abortController: AbortController;
  response: R;
};
export type PreRequestStage = {
  phase: "pre";
  abortController: AbortController;
};
export type PostRequestStage<R> = {
  phase: "post";
  abortController: AbortController;
  response: R;
};
export type TimeoutRequestStage = {
  phase: "timeout";
  abortController: AbortController;
};
export type AbortableRequestStages<R> =
  | PreRequestStage
  | PostRequestStage<R>
  | RespondedRequestStage<R>
  | ErrorRequestStage
  | TimeoutRequestStage;

export const useLoadingStore = defineStore("counter", () => {
  const loaders: Ref<Set<string>> = ref(new Set());
  const timeouts = new Map<string, NodeJS.Timeout>();
  const abortControllers = new Map<string, AbortController>();

  function startLoader(key: string, timeout = 1200000, onTimeout?: () => void): void {
    loaders.value.add(key);
    // ! Default 120 second timeout. If expected to last shorter/longer, gotta pass the expectation!
    const timerId = setTimeout(() => {
      loaders.value.delete(key);
      timeouts.delete(key);
      abortControllers.delete(key);
      if (onTimeout) {
        onTimeout();
      }
    }, timeout);
    timeouts.set(key, timerId);
  }
  function stopLoader(key: string): void {
    loaders.value.delete(key);
    clearTimeout(timeouts.get(key));
    timeouts.delete(key);
    abortControllers.delete(key);
  }
  const isLoaderRunning = (key: string): boolean => {
    return loaders.value.has(key);
  };
  const isLoading = computed(() => {
    return loaders.value.size > 0;
  });
  /**
   * Wraps an asynchronous function with loading state management, timeout, and abort capabilities.
   * This is an async generator that yields objects representing different phases of the request lifecycle.
   * If a loader with the same key is already running, it will be aborted before the new one starts.
   *
   * @template R The type of the response from the async function `fn`.
   * @param {string} key A unique key to identify and manage the loading state of the operation.
   * @param {function({ signal?: AbortSignal }): Promise<R>} fn The asynchronous function to execute. It receives an object containing an AbortSignal.
   * @param {number} [timeoutMilliseconds=120000] The duration in milliseconds before the operation times out and is aborted. Defaults to 2 minutes.
   * @returns {AsyncGenerator<AbortableRequestStages<R>>} An async generator that yields phase objects.
   */
  const abortableLoad = async function* <R>(
    key: string,
    fn: ({ signal }: { signal?: AbortSignal }) => Promise<R>,
    timeoutMilliseconds: number = 120000,
  ): AsyncGenerator<AbortableRequestStages<R>> {
    const abortController = new AbortController();
    let hasTimedOut = false;
    try {
      if (isLoaderRunning(key)) {
        const previousController = abortControllers.get(key);
        if (previousController) {
          previousController.abort();
        }
        stopLoader(key);
      }
      abortControllers.set(key, abortController);

      startLoader(key, timeoutMilliseconds, () => {
        abortController.abort();
        hasTimedOut = true;
      });
      yield {
        phase: "pre",
        abortController,
      };
      const response = await fn({ signal: abortController.signal });
      yield {
        phase: "post",
        abortController,
        response,
      };
      if (abortControllers.get(key) === abortController) {
        stopLoader(key);
      }
      yield {
        phase: "responded",
        abortController,
        response,
      };
    } catch (err) {
      const isAbortError =
        err instanceof Error &&
        (err.name === "AbortError" ||
          err.message.toLowerCase().includes("cancelled") ||
          err.message.toLowerCase().includes("canceled"));

      if (!isAbortError) {
        reportException(err);
      }

      if (abortControllers.get(key) === abortController) {
        stopLoader(key);
      }
      yield {
        phase: "error",
        abortController,
        error: err as Error,
      };
    } finally {
      if (hasTimedOut) {
        yield {
          phase: "timeout",
          abortController,
        };
      }
    }
  };
  /**
   * Consumes the async generator from `abortableLoad` and executes callbacks for each lifecycle phase.
   * This provides a simple way to handle the different stages of an abortable request.
   *
   * @template R The type of the response from the original async function.
   * @param {object} options The options for executing the abortable load.
   * @param {AsyncGenerator<AbortableRequestStages<R>>} options.abortableStagedLoad The async generator returned by `abortableLoad`.
   * @param {(stage: PreRequestStage) => void} [options.onPre] Optional callback executed during the 'pre' phase, before the async function is called.
   * @param {(stage: PostRequestStage<R>) => void} [options.onPost] Optional callback executed during the 'post' phase, after the async function has resolved but before the loader is stopped.
   * @param {(stage: RespondedRequestStage<R>) => void} options.onResponded Callback executed during the 'responded' phase, after the loader has been stopped.
   * @param {(stage: ErrorRequestStage) => void} options.onError Callback executed if an error occurs.
   * @param {(stage: TimeoutRequestStage) => void} [options.onTimeout] Optional callback executed if the operation times out.
   * @returns {Promise<void>} A promise that resolves when all stages have been processed.
   */
  return {
    loaders,
    startLoader,
    stopLoader,
    isLoading,
    isLoaderRunning,
    abortableLoad,
    executeAbortableLoad,
  };
});
